import { useEffect, useMemo, useState } from 'react';
import { useProgramReportCard } from '../../hooks/useProgramReportCard';
import {
  SectionBlock,
  FieldRow
} from '../../features/quality-check/shared';
import { ReportCardHeader } from '../../features/quality-check/report-card/ReportCardHeader';
import { useUpdateProgramMedia } from '../../hooks/useProgramMedia';
import { useUpdateProgramContacts } from '../../hooks/useProgramContacts';
import { useUpdateProgramFeesPrizes } from '../../hooks/useProgramFeesPrizes';
import {
  useUpdateProgramAiInstructions,
  useUpdateProgramStep0Flags,
  useUpdateProgramRequirementsQc,
  useUpdateProgramDeadlinesQc,
  useUpdateProgramJudgingQc,
  useUpdateProgramDetailsQc,
  useUpdateProgramVettingQc,
  useUpdateProgramDetailsData
} from '../../hooks/useProgramDetails';
import { VettingService } from '../../services/vetting-service';
import { supabase } from '../../utils/supabaseClient';
import styles from './ProgramCardPrint.module.css';
import type { VettingStatus } from '../../types/vetting';

// Utility: parse hash query parameters: #program-card?program_code=ABC
function getHashQuery(): Record<string, string> {
  const hash = window.location.hash || '';
  const query = hash.split('?')[1] || '';
  const params = new URLSearchParams(query);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

const toNullableString = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

const parseOptionalNumber = (value: string | null | undefined): number | null => {
  const normalized = toNullableString(value ?? null);
  if (normalized == null) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseOptionalBoolean = (value: string | null | undefined): boolean | null => {
  const normalized = toNullableString(value ?? null);
  if (normalized == null) return null;
  const lower = normalized.toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(lower)) return true;
  if (['no', 'n', 'false', '0'].includes(lower)) return false;
  return null;
};

const formatBooleanDisplay = (value: boolean | null | undefined): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not provided';
};

const formatArrayDisplay = (value: string[] | null | undefined): string => {
  if (!value || value.length === 0) return '[empty]';
  return value.join(', ');
};

type SectionKey =
  | 'snapshot'
  | 'preparation'
  | 'media'
  | 'contacts'
  | 'fees'
  | 'requirements'
  | 'deadlines'
  | 'judging'
  | 'details'
  | 'vetting';

type BannerState = {
  type: 'success' | 'error';
  message: string;
};

type MediaDraft = {
  website_url: string;
  submission_url: string;
  past_winners_url: string;
  logo_url: string;
  website_screenshot_url: string;
  seed_urls: string;
  wikipedia_entry: string;
  categories_url: string;
  deadlines_url: string;
  fees_url: string;
  requirements_url: string;
  contact_url: string;
  guidelines_url: string;
  qc_flag_media: boolean | null;
  qc_flag_media_notes: string | null;
};

type ContactsDraft = {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  physical_address: string;
  contact_notes: string;
  qc_flag_contacts: boolean | null;
  qc_flag_contacts_notes: string | null;
};

type FeesDraft = {
  entry_fee: string;
  entry_fee_currency: string;
  cost_range: string;
  prize_structure: string;
  prizes_per_category: string;
  total_win_opportunities: string;
  primary_currency: string;
  prize_types: string;
  has_tiered_prizes: string;
  extra_benefits: string;
  qc_flag_fees_prizes: boolean | null;
  qc_flag_fees_prizes_notes: string | null;
};

type SnapshotDraft = {
  program_name: string;
  awarding_organization: string;
  foundation_year: string;
  host_country: string;
};

type DetailsDraft = {
  host_country: string;
  language_requirements: string;
  submission_type: string;
  has_audiobook_categories: string;
  bisac_default_primary_code: string;
  bisac_default_secondary_code: string;
  bisac_default_tertiary_code: string;
  bisac_preferred_edition_year: string;
  industry_warnings: string;
  qc_flag_program_details: boolean | null;
  qc_flag_program_details_notes: string | null;
};

type PreparationDraft = {
  ai_instructions: string;
  qc_flag_preparation: boolean | null;
  qc_flag_preparation_notes: string | null;
};

type FlagDraft = {
  flag: boolean | null;
  notes: string | null;
};

type VettingDraft = {
  vetting_notes: string;
  qc_flag_vetting: boolean | null;
  qc_flag_vetting_notes: string | null;
};

const vettingOptions: Array<{ label: string; status: VettingStatus; description: string }> = [
  { label: 'Approved', status: 'green', description: 'Ready to recommend to clients' },
  { label: 'Needs Review', status: 'yellow', description: 'Requires additional validation' },
  { label: 'Unsuitable', status: 'red', description: 'Do not recommend for submissions' }
];

const ProgramCardPrint: React.FC = () => {
  const [banner, setBanner] = useState<BannerState | null>(null);
  const [editorId, setEditorId] = useState<string | null>(null);
  const [currentEditSection, setCurrentEditSection] = useState<SectionKey | null>(null);
  const [savingSection, setSavingSection] = useState<SectionKey | null>(null);

  const [snapshotDraft, setSnapshotDraft] = useState<SnapshotDraft | null>(null);
  const [mediaDraft, setMediaDraft] = useState<MediaDraft | null>(null);
  const [contactsDraft, setContactsDraft] = useState<ContactsDraft | null>(null);
  const [feesDraft, setFeesDraft] = useState<FeesDraft | null>(null);
  const [detailsDraft, setDetailsDraft] = useState<DetailsDraft | null>(null);
  const [preparationDraft, setPreparationDraft] = useState<PreparationDraft | null>(null);
  const [requirementsDraft, setRequirementsDraft] = useState<FlagDraft | null>(null);
  const [deadlinesDraft, setDeadlinesDraft] = useState<FlagDraft | null>(null);
  const [judgingDraft, setJudgingDraft] = useState<FlagDraft | null>(null);
  const [vettingDraft, setVettingDraft] = useState<VettingDraft | null>(null);

  const programCode = useMemo(() => {
    const params = getHashQuery();
    return params['program_code'] || '';
  }, []);

  const vettingService = useMemo(() => new VettingService(), []);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      setEditorId(data?.user?.id ?? null);
    }).catch(() => {
      if (!cancelled) setEditorId(null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching
  } = useProgramReportCard(programCode, {
    enabled: programCode.length > 0
  });

  const updateMediaMutation = useUpdateProgramMedia();
  const updateContactsMutation = useUpdateProgramContacts();
  const updateFeesPrizesMutation = useUpdateProgramFeesPrizes();
  const updateAiInstructionsMutation = useUpdateProgramAiInstructions();
  const updateStep0FlagsMutation = useUpdateProgramStep0Flags();
  const updateRequirementsQcMutation = useUpdateProgramRequirementsQc();
  const updateDeadlinesQcMutation = useUpdateProgramDeadlinesQc();
  const updateJudgingQcMutation = useUpdateProgramJudgingQc();
  const updateProgramDetailsQcMutation = useUpdateProgramDetailsQc();
  const updateProgramDetailsDataMutation = useUpdateProgramDetailsData();
  const updateProgramVettingQcMutation = useUpdateProgramVettingQc();

  const categoriesByBucket = useMemo(() => {
    if (!data?.categories || data.categories.length === 0) {
      return {
        adult: { total: 0, fiction: 0, nonFiction: 0, poetry: 0, technical: 0, unclassified: 0, categories: [] },
        youngAdult: { total: 0, fiction: 0, nonFiction: 0, poetry: 0, technical: 0, unclassified: 0, categories: [] },
        children: { total: 0, fiction: 0, nonFiction: 0, poetry: 0, technical: 0, unclassified: 0, categories: [] },
        noBucket: { total: 0, categories: [] }
      };
    }

    type BucketGroup = {
      total: number;
      fiction: number;
      nonFiction: number;
      poetry: number;
      technical: number;
      unclassified: number;
      categories: typeof data.categories;
    };

    const result = {
      adult: { total: 0, fiction: 0, nonFiction: 0, poetry: 0, technical: 0, unclassified: 0, categories: [] } as BucketGroup,
      youngAdult: { total: 0, fiction: 0, nonFiction: 0, poetry: 0, technical: 0, unclassified: 0, categories: [] } as BucketGroup,
      children: { total: 0, fiction: 0, nonFiction: 0, poetry: 0, technical: 0, unclassified: 0, categories: [] } as BucketGroup,
      noBucket: { total: 0, categories: [] } as { total: number; categories: typeof data.categories }
    };

    data.categories.forEach((cat) => {
      const bucket1 = (cat.bucket_level_1 ?? '').toLowerCase();
      const bucket2 = (cat.bucket_level_2 ?? '').toLowerCase();

      let targetBucket: BucketGroup | null = null;

      if (bucket1 === 'adult') targetBucket = result.adult;
      else if (bucket1 === 'young adult' || bucket1 === 'youngadult') targetBucket = result.youngAdult;
      else if (bucket1 === 'children') targetBucket = result.children;

      if (targetBucket) {
        targetBucket.total++;
        targetBucket.categories.push(cat);

        if (bucket2.includes('fiction') && !bucket2.includes('non')) targetBucket.fiction++;
        else if (bucket2.includes('non-fiction') || bucket2.includes('nonfiction')) targetBucket.nonFiction++;
        else if (bucket2.includes('poetry')) targetBucket.poetry++;
        else if (bucket2.includes('technical')) targetBucket.technical++;
        else targetBucket.unclassified++;
      } else {
        result.noBucket.total++;
        result.noBucket.categories.push(cat);
      }
    });

    return result;
  }, [data?.categories]);

  const qcWorkflowUrl = useMemo(() => {
    if (!data) return null;
    return `#quality-checkv2?program_id=${encodeURIComponent(data.program_id)}&program_code=${encodeURIComponent(data.baci_program_code)}`;
  }, [data]);

  const isEditing = (section: SectionKey) => currentEditSection === section;

  const resetDrafts = () => {
    setSnapshotDraft(null);
    setMediaDraft(null);
    setContactsDraft(null);
    setFeesDraft(null);
    setDetailsDraft(null);
    setPreparationDraft(null);
    setRequirementsDraft(null);
    setDeadlinesDraft(null);
    setJudgingDraft(null);
    setVettingDraft(null);
  };

  const handleEdit = (section: SectionKey) => {
    if (!data) return;
    setBanner(null);
    resetDrafts();

    switch (section) {
      case 'snapshot':
        setSnapshotDraft({
          program_name: data.program_name ?? '',
          awarding_organization: data.awarding_organization ?? '',
          foundation_year: data.founding_year != null ? String(data.founding_year) : '',
          host_country: data.host_country ?? ''
        });
        break;
      case 'media':
        setMediaDraft({
          website_url: data.website_url ?? '',
          submission_url: data.submission_url ?? '',
          past_winners_url: data.past_winners_url ?? '',
          logo_url: data.logo_url ?? '',
          website_screenshot_url: data.website_screenshot_url ?? '',
          seed_urls: data.seed_urls ?? '',
          wikipedia_entry: data.wikipedia_entry ?? '',
          categories_url: data.categories_url ?? '',
          deadlines_url: data.deadlines_url ?? '',
          fees_url: data.fees_url ?? '',
          requirements_url: data.requirements_url ?? '',
          contact_url: data.contact_url ?? '',
          guidelines_url: data.guidelines_url ?? '',
          qc_flag_media: data.qc_flag_media ?? null,
          qc_flag_media_notes: data.qc_flag_media_notes ?? null
        });
        break;
      case 'contacts':
        setContactsDraft({
          contact_name: data.contact_name ?? '',
          contact_email: data.contact_email ?? '',
          contact_phone: data.contact_phone ?? '',
          physical_address: data.physical_address ?? '',
          contact_notes: data.contact_notes ?? '',
          qc_flag_contacts: data.qc_flag_contacts ?? null,
          qc_flag_contacts_notes: data.qc_flag_contacts_notes ?? null
        });
        break;
      case 'fees':
        setFeesDraft({
          entry_fee: data.entry_fee != null ? String(data.entry_fee) : '',
          entry_fee_currency: data.entry_fee_currency ?? '',
          cost_range: data.cost_range ?? '',
          prize_structure: data.prize_structure ? JSON.stringify(data.prize_structure, null, 2) : '',
          prizes_per_category: data.prizes_per_category != null ? String(data.prizes_per_category) : '',
          total_win_opportunities: data.total_win_opportunities != null ? String(data.total_win_opportunities) : '',
          primary_currency: data.primary_currency ?? '',
          prize_types: data.prize_types ? data.prize_types.join(', ') : '',
          has_tiered_prizes: data.has_tiered_prizes != null ? (data.has_tiered_prizes ? 'yes' : 'no') : '',
          extra_benefits: data.extra_benefits ?? '',
          qc_flag_fees_prizes: data.qc_flag_fees_prizes ?? null,
          qc_flag_fees_prizes_notes: data.qc_flag_fees_prizes_notes ?? null
        });
        break;
      case 'details':
        setDetailsDraft({
          host_country: data.host_country ?? '',
          language_requirements: data.language_requirements ?? '',
          submission_type: data.submission_type ?? '',
          has_audiobook_categories: data.has_audiobook_categories != null ? (data.has_audiobook_categories ? 'yes' : 'no') : '',
          bisac_default_primary_code: data.bisac_default_primary_code ?? '',
          bisac_default_secondary_code: data.bisac_default_secondary_code ?? '',
          bisac_default_tertiary_code: data.bisac_default_tertiary_code ?? '',
          bisac_preferred_edition_year: data.bisac_preferred_edition_year ?? '',
          industry_warnings: data.industry_warnings ?? '',
          qc_flag_program_details: data.qc_flag_program_details ?? null,
          qc_flag_program_details_notes: data.qc_flag_program_details_notes ?? null
        });
        break;
      case 'preparation':
        setPreparationDraft({
          ai_instructions: data.ai_instructions ?? '',
          qc_flag_preparation: data.qc_flag_preparation ?? null,
          qc_flag_preparation_notes: data.qc_flag_preparation_notes ?? null
        });
        break;
      case 'requirements':
        setRequirementsDraft({
          flag: data.qc_flag_requirements ?? null,
          notes: data.qc_flag_requirements_notes ?? null
        });
        break;
      case 'deadlines':
        setDeadlinesDraft({
          flag: data.qc_flag_deadlines ?? null,
          notes: data.qc_flag_deadlines_notes ?? null
        });
        break;
      case 'judging':
        setJudgingDraft({
          flag: data.qc_flag_judging ?? null,
          notes: data.qc_flag_judging_notes ?? null
        });
        break;
      case 'vetting':
        setVettingDraft({
          vetting_notes: data.vetting_notes ?? '',
          qc_flag_vetting: data.qc_flag_vetting ?? null,
          qc_flag_vetting_notes: data.qc_flag_vetting_notes ?? null
        });
        break;
      default:
        break;
    }

    setCurrentEditSection(section);
  };

  const handleCancel = () => {
    resetDrafts();
    setCurrentEditSection(null);
    setBanner(null);
  };

  const finishSuccessfulSave = async (message: string) => {
    setBanner({ type: 'success', message });
    setCurrentEditSection(null);
    resetDrafts();
    await refetch();
  };

  const handleSave = async (section: SectionKey) => {
    if (!data) return;
    setSavingSection(section);
    setBanner(null);

    try {
      switch (section) {
        case 'snapshot': {
          if (!snapshotDraft) break;

          const normalizedName = toNullableString(snapshotDraft.program_name);

          if (!normalizedName) {
            setBanner({ type: 'error', message: 'Program name cannot be empty.' });
            setSavingSection(null);
            return;
          }

          const normalizedOrg = toNullableString(snapshotDraft.awarding_organization);
          const foundationYearInput = toNullableString(snapshotDraft.foundation_year);

          let foundationYearValue: number | null = null;
          if (foundationYearInput) {
            const parsedYear = Number.parseInt(foundationYearInput, 10);
            if (Number.isNaN(parsedYear)) {
              setBanner({ type: 'error', message: 'Foundation year must be a valid number.' });
              setSavingSection(null);
              return;
            }
            foundationYearValue = parsedYear;
          }

          const { error: overviewError } = await supabase
            .schema('prod')
            .from('programs')
            .update({
              name: normalizedName,
              awarding_organization: normalizedOrg,
              foundation_year: foundationYearValue
            })
            .eq('id', data.program_id);

          if (overviewError) {
            throw new Error(overviewError.message || 'Failed to update program overview');
          }

          const normalizedHostCountry = toNullableString(snapshotDraft.host_country);
          const existingHostCountry = toNullableString(data.host_country ?? null);

          if (normalizedHostCountry !== existingHostCountry) {
            await updateProgramDetailsDataMutation.mutateAsync({
              program_id: data.program_id,
              host_country: normalizedHostCountry,
              language_requirements: toNullableString(data.language_requirements ?? null),
              submission_type: toNullableString(data.submission_type ?? null),
              has_audiobook_categories: data.has_audiobook_categories ?? null,
              bisac_default_primary_code: toNullableString(data.bisac_default_primary_code ?? null),
              bisac_default_secondary_code: toNullableString(data.bisac_default_secondary_code ?? null),
              bisac_default_tertiary_code: toNullableString(data.bisac_default_tertiary_code ?? null),
              bisac_preferred_edition_year: toNullableString(data.bisac_preferred_edition_year ?? null),
              industry_warnings: toNullableString(data.industry_warnings ?? null),
              editor_id: editorId
            });
          }

          await finishSuccessfulSave('Program snapshot updated.');
          break;
        }
        case 'preparation': {
          if (!preparationDraft) break;
          const aiInstructions = toNullableString(preparationDraft.ai_instructions);
          const existingAi = toNullableString(data.ai_instructions ?? null);

          if (aiInstructions !== existingAi) {
            await updateAiInstructionsMutation.mutateAsync({
              program_id: data.program_id,
              ai_instructions: aiInstructions,
              editor_id: editorId
            });
          }

          const flag = preparationDraft.qc_flag_preparation ?? false;
          const existingFlag = data.qc_flag_preparation ?? false;
          const notes = toNullableString(preparationDraft.qc_flag_preparation_notes ?? null);
          const existingNotes = toNullableString(data.qc_flag_preparation_notes ?? null);

          if (flag !== existingFlag || notes !== existingNotes) {
            await updateStep0FlagsMutation.mutateAsync({
              program_id: data.program_id,
              qc_flag_preparation: flag,
              qc_flag_preparation_notes: notes,
              editor_id: editorId
            });
          }

          await finishSuccessfulSave('Preparation details updated.');
          break;
        }
        case 'media': {
          if (!mediaDraft) break;
          await updateMediaMutation.mutateAsync({
            program_id: data.program_id,
            website_url: toNullableString(mediaDraft.website_url),
            submission_url: toNullableString(mediaDraft.submission_url),
            past_winners_url: toNullableString(mediaDraft.past_winners_url),
            logo_url: toNullableString(mediaDraft.logo_url),
            website_screenshot_url: toNullableString(mediaDraft.website_screenshot_url),
            seed_urls: toNullableString(mediaDraft.seed_urls),
            wikipedia_entry: toNullableString(mediaDraft.wikipedia_entry),
            categories_url: toNullableString(mediaDraft.categories_url),
            deadlines_url: toNullableString(mediaDraft.deadlines_url),
            fees_url: toNullableString(mediaDraft.fees_url),
            requirements_url: toNullableString(mediaDraft.requirements_url),
            contact_url: toNullableString(mediaDraft.contact_url),
            guidelines_url: toNullableString(mediaDraft.guidelines_url),
            qc_flag_media: mediaDraft.qc_flag_media ?? null,
            qc_flag_media_notes: toNullableString(mediaDraft.qc_flag_media_notes ?? null),
            editor_id: editorId
          });
          await finishSuccessfulSave('Media & URLs updated.');
          break;
        }
        case 'contacts': {
          if (!contactsDraft) break;
          await updateContactsMutation.mutateAsync({
            program_id: data.program_id,
            contact_name: toNullableString(contactsDraft.contact_name),
            contact_email: toNullableString(contactsDraft.contact_email),
            contact_phone: toNullableString(contactsDraft.contact_phone),
            physical_address: toNullableString(contactsDraft.physical_address),
            contact_notes: toNullableString(contactsDraft.contact_notes),
            qc_flag_contacts: contactsDraft.qc_flag_contacts ?? null,
            qc_flag_contacts_notes: toNullableString(contactsDraft.qc_flag_contacts_notes ?? null),
            editor_id: editorId
          });
          await finishSuccessfulSave('Contacts updated.');
          break;
        }
        case 'fees': {
          if (!feesDraft) break;

          let prizeStructure: Record<string, unknown> | null = null;
          const prizeStructureString = toNullableString(feesDraft.prize_structure);
          if (prizeStructureString) {
            try {
              prizeStructure = JSON.parse(prizeStructureString) as Record<string, unknown>;
            } catch (_error) {
              throw new Error('Prize structure must be valid JSON.');
            }
          }

          const prizeTypes = toNullableString(feesDraft.prize_types);
          await updateFeesPrizesMutation.mutateAsync({
            program_id: data.program_id,
            entry_fee: parseOptionalNumber(feesDraft.entry_fee),
            entry_fee_currency: toNullableString(feesDraft.entry_fee_currency),
            cost_range: toNullableString(feesDraft.cost_range),
            prize_structure: prizeStructure,
            prizes_per_category: parseOptionalNumber(feesDraft.prizes_per_category),
            total_win_opportunities: parseOptionalNumber(feesDraft.total_win_opportunities),
            primary_currency: toNullableString(feesDraft.primary_currency),
            prize_types: prizeTypes ? prizeTypes.split(',').map((item) => item.trim()).filter(Boolean) : null,
            has_tiered_prizes: parseOptionalBoolean(feesDraft.has_tiered_prizes),
            extra_benefits: toNullableString(feesDraft.extra_benefits),
            qc_flag_fees_prizes: feesDraft.qc_flag_fees_prizes ?? null,
            qc_flag_fees_prizes_notes: toNullableString(feesDraft.qc_flag_fees_prizes_notes ?? null),
            editor_id: editorId ?? undefined
          });
          await finishSuccessfulSave('Fees & prizes updated.');
          break;
        }
        case 'requirements': {
          if (!requirementsDraft) break;
          await updateRequirementsQcMutation.mutateAsync({
            program_id: data.program_id,
            qc_flag_requirements: requirementsDraft.flag ?? false,
            qc_flag_requirements_notes: toNullableString(requirementsDraft.notes ?? null),
            editor_id: editorId
          });
          await finishSuccessfulSave('Requirements QC notes updated.');
          break;
        }
        case 'deadlines': {
          if (!deadlinesDraft) break;
          await updateDeadlinesQcMutation.mutateAsync({
            program_id: data.program_id,
            qc_flag_deadlines: deadlinesDraft.flag ?? false,
            qc_flag_deadlines_notes: toNullableString(deadlinesDraft.notes ?? null),
            editor_id: editorId
          });
          await finishSuccessfulSave('Deadlines QC notes updated.');
          break;
        }
        case 'judging': {
          if (!judgingDraft) break;
          await updateJudgingQcMutation.mutateAsync({
            program_id: data.program_id,
            qc_flag_judging: judgingDraft.flag ?? false,
            qc_flag_judging_notes: toNullableString(judgingDraft.notes ?? null),
            editor_id: editorId
          });
          await finishSuccessfulSave('Judging QC notes updated.');
          break;
        }
        case 'details': {
          if (!detailsDraft) break;
          await updateProgramDetailsDataMutation.mutateAsync({
            program_id: data.program_id,
            host_country: toNullableString(detailsDraft.host_country),
            language_requirements: toNullableString(detailsDraft.language_requirements),
            submission_type: toNullableString(detailsDraft.submission_type),
            has_audiobook_categories: parseOptionalBoolean(detailsDraft.has_audiobook_categories),
            bisac_default_primary_code: toNullableString(detailsDraft.bisac_default_primary_code),
            bisac_default_secondary_code: toNullableString(detailsDraft.bisac_default_secondary_code),
            bisac_default_tertiary_code: toNullableString(detailsDraft.bisac_default_tertiary_code),
            bisac_preferred_edition_year: toNullableString(detailsDraft.bisac_preferred_edition_year),
            industry_warnings: toNullableString(detailsDraft.industry_warnings),
            editor_id: editorId
          });

          await updateProgramDetailsQcMutation.mutateAsync({
            program_id: data.program_id,
            qc_flag_program_details: detailsDraft.qc_flag_program_details ?? false,
            qc_flag_program_details_notes: toNullableString(detailsDraft.qc_flag_program_details_notes ?? null),
            editor_id: editorId
          });

          await finishSuccessfulSave('Program details updated.');
          break;
        }
        case 'vetting': {
          if (!vettingDraft) break;

          // Update vetting notes directly
          const normalizedNotes = toNullableString(vettingDraft.vetting_notes);
          const existingNotes = toNullableString(data.vetting_notes ?? null);

          if (normalizedNotes !== existingNotes) {
            const { error: vettingError } = await supabase
              .schema('prod')
              .from('vetting')
              .update({ vetting_notes: normalizedNotes })
              .eq('program_id', data.program_id);

            if (vettingError) {
              throw new Error(vettingError.message || 'Failed to update vetting notes');
            }
          }

          // Update QC flags
          await updateProgramVettingQcMutation.mutateAsync({
            program_id: data.program_id,
            qc_flag_vetting: vettingDraft.qc_flag_vetting ?? false,
            qc_flag_vetting_notes: toNullableString(vettingDraft.qc_flag_vetting_notes ?? null),
            editor_id: editorId
          });

          await finishSuccessfulSave('Vetting details updated.');
          break;
        }
        default:
          break;
      }
    } catch (saveError) {
      setBanner({ type: 'error', message: getErrorMessage(saveError, 'Failed to save changes.') });
    } finally {
      setSavingSection(null);
    }
  };

  const handleVettingStatusChange = async (status: VettingStatus) => {
    if (!data) return;
    try {
      setSavingSection('vetting');
      const response = await vettingService.setVettingStatus(
        data.program_id,
        status,
        'program-card-update'
      );
      if (!response.success) {
        throw new Error(response.error ?? 'Unknown vetting update error');
      }
      setBanner({ type: 'success', message: 'Vetting status updated.' });
      await refetch();
    } catch (statusError) {
      setBanner({ type: 'error', message: getErrorMessage(statusError, 'Failed to update vetting status.') });
    } finally {
      setSavingSection(null);
    }
  };

  if (!programCode) {
    return (
      <div className={styles.root}>
        <div className={styles.loader}>Missing program_code in URL.</div>
      </div>
    );
  }

  if (isLoading || isRefetching) {
    return (
      <div className={styles.root}>
        <div className={styles.loader}>Loading program card…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.layout}>
          <div className={styles.errorBox}>{error.message}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.root}>
        <div className={styles.layout}>
          <div className={styles.errorBox}>No program data found.</div>
        </div>
      </div>
    );
  }

  const sectionAction = (section: SectionKey) => {
    if (currentEditSection === section) {
      return (
        <div className={styles.sectionActions}>
          <button
            type="button"
            className={styles.button}
            onClick={handleCancel}
            disabled={savingSection === section}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={() => void handleSave(section)}
            disabled={savingSection === section}
          >
            {savingSection === section ? 'Saving…' : 'Save'}
          </button>
        </div>
      );
    }

    return (
      <div className={styles.sectionActions}>
        <button
          type="button"
          className={styles.button}
          onClick={() => handleEdit(section)}
        >
          Edit
        </button>
      </div>
    );
  };

  const readOnlyAction = () => {
    return null;
  };

  return (
    <div className={styles.root}>
      <div className={styles.layout}>
        <a href="#data-quality" className={styles.backLink}>
          ← Back to Data Quality
        </a>

        {banner && (
          <div
            className={`${styles.banner} ${
              banner.type === 'success' ? styles.bannerSuccess : styles.bannerError
            }`}
          >
            {banner.message}
          </div>
        )}

        <div className={styles.topBar}>
          <button
            type="button"
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={() => window.print()}
          >
            Print Report Card
          </button>
          {qcWorkflowUrl && (
            <button
              type="button"
              className={`${styles.button} ${styles.outlineButton}`}
              onClick={() => {
                window.location.hash = qcWorkflowUrl;
              }}
            >
              Open QC Workflow
            </button>
          )}
        </div>

        <ReportCardHeader
          programName={data.program_name}
          baciCode={data.baci_program_code}
          organization={data.awarding_organization}
          foundingYear={data.founding_year}
          email={data.contact_email}
          phone={data.contact_phone}
          website={data.website_url}
          showPrintButton={false}
        />

        <SectionBlock
          title="Program Snapshot"
          icon="🧭"
          secondaryAction={sectionAction('snapshot')}
        >
          <div className={styles.snapshotGrid}>
            <FieldRow
              label="Program Name"
              value={isEditing('snapshot') && snapshotDraft ? snapshotDraft.program_name : data.program_name}
              editable={isEditing('snapshot')}
              onChange={(value) => {
                if (!isEditing('snapshot')) return;
                setSnapshotDraft((draft) => (draft ? { ...draft, program_name: value } : null));
              }}
            />
            <FieldRow
              label="Awarding Organization"
              value={isEditing('snapshot') && snapshotDraft ? snapshotDraft.awarding_organization : data.awarding_organization}
              editable={isEditing('snapshot')}
              onChange={(value) => {
                if (!isEditing('snapshot')) return;
                setSnapshotDraft((draft) => (draft ? { ...draft, awarding_organization: value } : null));
              }}
            />
            <FieldRow
              label="Foundation Year"
              value={isEditing('snapshot') && snapshotDraft ? snapshotDraft.foundation_year : (data.founding_year != null ? String(data.founding_year) : '')}
              editable={isEditing('snapshot')}
              onChange={(value) => {
                if (!isEditing('snapshot')) return;
                setSnapshotDraft((draft) => (draft ? { ...draft, foundation_year: value } : null));
              }}
            />
            <FieldRow
              label="Host Country"
              value={isEditing('snapshot') && snapshotDraft ? snapshotDraft.host_country : data.host_country}
              editable={isEditing('snapshot')}
              onChange={(value) => {
                if (!isEditing('snapshot')) return;
                setSnapshotDraft((draft) => (draft ? { ...draft, host_country: value } : null));
              }}
            />
            <FieldRow
              label="Number of Categories"
              value={data.number_of_categories ?? (data.categories?.length ?? 0)}
            />
            <FieldRow
              label="Number of Win Opportunities"
              value={data.total_win_opportunities ?? '[empty]'}
            />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Preparation & AI Brief"
          icon="🧠"
          secondaryAction={sectionAction('preparation')}
        >
          <div className={styles.aiInstructionsBox}>
            <FieldRow
              label="AI Instructions"
              value={isEditing('preparation') && preparationDraft ? preparationDraft.ai_instructions : data.ai_instructions}
              editable={isEditing('preparation')}
              multiline
              onChange={(value) => {
                if (!isEditing('preparation')) return;
                setPreparationDraft((draft) => draft ? { ...draft, ai_instructions: value } : {
                  ai_instructions: value,
                  qc_flag_preparation: data.qc_flag_preparation ?? null,
                  qc_flag_preparation_notes: data.qc_flag_preparation_notes ?? null
                });
              }}
            />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Primary URLs & Media"
          icon="🌐"
          secondaryAction={sectionAction('media')}
        >
          <div className={styles.urlsGrid}>
            <div className={styles.urlField}>
              <span className={styles.urlLabel}>Website URL:</span>
              {isEditing('media') ? (
                <input
                  type="text"
                  className={styles.urlInput}
                  value={mediaDraft?.website_url ?? ''}
                  onChange={(e) => setMediaDraft((draft) => draft ? { ...draft, website_url: e.target.value } : null)}
                  placeholder="Enter website URL..."
                />
              ) : (
                data.website_url ? (
                  <a href={data.website_url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    {data.website_url}
                  </a>
                ) : (
                  <span className={styles.urlEmpty}>[empty]</span>
                )
              )}
            </div>
            <div className={styles.urlField}>
              <span className={styles.urlLabel}>Submission URL:</span>
              {isEditing('media') ? (
                <input
                  type="text"
                  className={styles.urlInput}
                  value={mediaDraft?.submission_url ?? ''}
                  onChange={(e) => setMediaDraft((draft) => draft ? { ...draft, submission_url: e.target.value } : null)}
                  placeholder="Enter submission URL..."
                />
              ) : (
                data.submission_url ? (
                  <a href={data.submission_url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    {data.submission_url}
                  </a>
                ) : (
                  <span className={styles.urlEmpty}>[empty]</span>
                )
              )}
            </div>
            <div className={styles.urlField}>
              <span className={styles.urlLabel}>Past Winners URL:</span>
              {isEditing('media') ? (
                <input
                  type="text"
                  className={styles.urlInput}
                  value={mediaDraft?.past_winners_url ?? ''}
                  onChange={(e) => setMediaDraft((draft) => draft ? { ...draft, past_winners_url: e.target.value } : null)}
                  placeholder="Enter past winners URL..."
                />
              ) : (
                data.past_winners_url ? (
                  <a href={data.past_winners_url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    {data.past_winners_url}
                  </a>
                ) : (
                  <span className={styles.urlEmpty}>[empty]</span>
                )
              )}
            </div>
            <div className={styles.urlField}>
              <span className={styles.urlLabel}>Guidelines URL:</span>
              {isEditing('media') ? (
                <input
                  type="text"
                  className={styles.urlInput}
                  value={mediaDraft?.guidelines_url ?? ''}
                  onChange={(e) => setMediaDraft((draft) => draft ? { ...draft, guidelines_url: e.target.value } : null)}
                  placeholder="Enter guidelines URL..."
                />
              ) : (
                data.guidelines_url ? (
                  <a href={data.guidelines_url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    {data.guidelines_url}
                  </a>
                ) : (
                  <span className={styles.urlEmpty}>[empty]</span>
                )
              )}
            </div>
            <div className={styles.urlField}>
              <span className={styles.urlLabel}>Categories URL:</span>
              {isEditing('media') ? (
                <input
                  type="text"
                  className={styles.urlInput}
                  value={mediaDraft?.categories_url ?? ''}
                  onChange={(e) => setMediaDraft((draft) => draft ? { ...draft, categories_url: e.target.value } : null)}
                  placeholder="Enter categories URL..."
                />
              ) : (
                data.categories_url ? (
                  <a href={data.categories_url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    {data.categories_url}
                  </a>
                ) : (
                  <span className={styles.urlEmpty}>[empty]</span>
                )
              )}
            </div>
            <div className={`${styles.urlField} ${styles.urlFieldFull}`}>
              <span className={styles.urlLabel}>Seed URLs:</span>
              {isEditing('media') ? (
                <textarea
                  className={styles.urlTextarea}
                  value={mediaDraft?.seed_urls ?? ''}
                  onChange={(e) => setMediaDraft((draft) => draft ? { ...draft, seed_urls: e.target.value } : null)}
                  placeholder="Enter comma-separated seed URLs..."
                  rows={3}
                />
              ) : (
                <span className={styles.urlValue}>{data.seed_urls || '[empty]'}</span>
              )}
            </div>
          </div>
        </SectionBlock>

        <SectionBlock
          title="Requirements"
          icon="📝"
          secondaryAction={sectionAction('requirements')}
        >
          <div className={styles.twoColGrid}>
            <FieldRow label="Self-Published Accepted" value={formatBooleanDisplay(data.self_published_accepted)} />
            <FieldRow label="ISBN Required" value={formatBooleanDisplay(data.isbn_required)} />
            <FieldRow label="Geographic Restrictions" value={data.geographic_restrictions} />
            <FieldRow label="Accepted Formats" value={data.accepted_formats} />
            <FieldRow label="Publication Requirements" value={data.publication_date_requirements} className={styles.fullWidth} multiline />
            <FieldRow label="Requirements Summary" value={data.requirements_summary} className={styles.fullWidth} multiline />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Deadlines"
          icon="📅"
          secondaryAction={sectionAction('deadlines')}
        >
          <div className={styles.twoColGrid}>
            <FieldRow label="Submission Period" value={data.submission_period} />
            <FieldRow label="Early Bird Deadline" value={data.early_bird_deadline} />
            <FieldRow label="Regular Deadline" value={data.regular_deadline} />
            <FieldRow label="Final Deadline" value={data.final_deadline} />
            <FieldRow label="Winners Announcement" value={data.winners_announcement_date} />
            <FieldRow label="Award Ceremony" value={data.award_ceremony_date} />
            <FieldRow label="Deadline Structure" value={data.deadline_structure} className={styles.fullWidth} multiline />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Fees & Prizes"
          icon="💰"
          secondaryAction={sectionAction('fees')}
        >
          <div className={styles.twoColGrid}>
            <FieldRow
              label="Entry Fee"
              value={isEditing('fees') && feesDraft ? feesDraft.entry_fee : data.entry_fee != null ? String(data.entry_fee) : ''}
              editable={isEditing('fees')}
              onChange={(value) => isEditing('fees') && setFeesDraft((draft) => draft ? { ...draft, entry_fee: value } : null)}
            />
            <FieldRow
              label="Entry Fee Currency"
              value={isEditing('fees') && feesDraft ? feesDraft.entry_fee_currency : data.entry_fee_currency}
              editable={isEditing('fees')}
              onChange={(value) => isEditing('fees') && setFeesDraft((draft) => draft ? { ...draft, entry_fee_currency: value } : null)}
            />
            <FieldRow
              label="Total Win Opportunities"
              value={isEditing('fees') && feesDraft ? feesDraft.total_win_opportunities : data.total_win_opportunities != null ? String(data.total_win_opportunities) : ''}
              editable={isEditing('fees')}
              onChange={(value) => isEditing('fees') && setFeesDraft((draft) => draft ? { ...draft, total_win_opportunities: value } : null)}
            />
            <FieldRow
              label="Prizes per Category"
              value={isEditing('fees') && feesDraft ? feesDraft.prizes_per_category : data.prizes_per_category != null ? String(data.prizes_per_category) : ''}
              editable={isEditing('fees')}
              onChange={(value) => isEditing('fees') && setFeesDraft((draft) => draft ? { ...draft, prizes_per_category: value } : null)}
            />
            <FieldRow
              label="Prize Types"
              value={isEditing('fees') && feesDraft ? feesDraft.prize_types : formatArrayDisplay(data.prize_types)}
              editable={isEditing('fees')}
              multiline
              onChange={(value) => isEditing('fees') && setFeesDraft((draft) => draft ? { ...draft, prize_types: value } : null)}
              className={styles.fullWidth}
            />
            <FieldRow
              label="Prize Structure (JSON)"
              value={isEditing('fees') && feesDraft ? feesDraft.prize_structure : data.prize_structure ? JSON.stringify(data.prize_structure, null, 2) : ''}
              editable={isEditing('fees')}
              multiline
              onChange={(value) => isEditing('fees') && setFeesDraft((draft) => draft ? { ...draft, prize_structure: value } : null)}
              className={styles.fullWidth}
            />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Categories"
          icon="📚"
          secondaryAction={readOnlyAction()}
        >
          {/* Bucket Summary - 4 columns */}
          <div className={styles.categoriesGrid}>
            <div className={styles.bucketColumn}>
              <h4 className={styles.bucketTitle}>Adult ({categoriesByBucket.adult.total})</h4>
              <ul className={styles.bucketList}>
                <li>Fiction: {categoriesByBucket.adult.fiction}</li>
                <li>Non-Fiction: {categoriesByBucket.adult.nonFiction}</li>
                <li>Poetry: {categoriesByBucket.adult.poetry}</li>
                <li>Technical: {categoriesByBucket.adult.technical}</li>
                <li>Unclassified: {categoriesByBucket.adult.unclassified}</li>
              </ul>
            </div>
            <div className={styles.bucketColumn}>
              <h4 className={styles.bucketTitle}>Young Adult ({categoriesByBucket.youngAdult.total})</h4>
              <ul className={styles.bucketList}>
                <li>Fiction: {categoriesByBucket.youngAdult.fiction}</li>
                <li>Non-Fiction: {categoriesByBucket.youngAdult.nonFiction}</li>
                <li>Poetry: {categoriesByBucket.youngAdult.poetry}</li>
                <li>Technical: {categoriesByBucket.youngAdult.technical}</li>
                <li>Unclassified: {categoriesByBucket.youngAdult.unclassified}</li>
              </ul>
            </div>
            <div className={styles.bucketColumn}>
              <h4 className={styles.bucketTitle}>Children ({categoriesByBucket.children.total})</h4>
              <ul className={styles.bucketList}>
                <li>Fiction: {categoriesByBucket.children.fiction}</li>
                <li>Non-Fiction: {categoriesByBucket.children.nonFiction}</li>
                <li>Poetry: {categoriesByBucket.children.poetry}</li>
                <li>Technical: {categoriesByBucket.children.technical}</li>
                <li>Unclassified: {categoriesByBucket.children.unclassified}</li>
              </ul>
            </div>
            <div className={styles.bucketColumn}>
              <h4 className={styles.bucketTitle}>No Bucket ({categoriesByBucket.noBucket.total})</h4>
            </div>
          </div>

          {/* Detailed Categories Table */}
          {data.categories.length > 0 ? (
            <div className={styles.categoriesTableWrapper}>
              <table className={styles.categoriesTable}>
                <thead>
                  <tr>
                    <th>Category Name</th>
                    <th>Bucket Level 1</th>
                    <th>Bucket Level 2</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Adult Categories */}
                  {categoriesByBucket.adult.categories.length > 0 && (
                    <>
                      <tr className={styles.bucketGroupHeader}>
                        <td colSpan={3}>Adult Categories ({categoriesByBucket.adult.total})</td>
                      </tr>
                      {categoriesByBucket.adult.categories.map((cat) => (
                        <tr key={cat.id}>
                          <td>{cat.name}</td>
                          <td>{cat.bucket_level_1 || '—'}</td>
                          <td>{cat.bucket_level_2 || '—'}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  {/* Young Adult Categories */}
                  {categoriesByBucket.youngAdult.categories.length > 0 && (
                    <>
                      <tr className={styles.bucketGroupHeader}>
                        <td colSpan={3}>Young Adult Categories ({categoriesByBucket.youngAdult.total})</td>
                      </tr>
                      {categoriesByBucket.youngAdult.categories.map((cat) => (
                        <tr key={cat.id}>
                          <td>{cat.name}</td>
                          <td>{cat.bucket_level_1 || '—'}</td>
                          <td>{cat.bucket_level_2 || '—'}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  {/* Children Categories */}
                  {categoriesByBucket.children.categories.length > 0 && (
                    <>
                      <tr className={styles.bucketGroupHeader}>
                        <td colSpan={3}>Children Categories ({categoriesByBucket.children.total})</td>
                      </tr>
                      {categoriesByBucket.children.categories.map((cat) => (
                        <tr key={cat.id}>
                          <td>{cat.name}</td>
                          <td>{cat.bucket_level_1 || '—'}</td>
                          <td>{cat.bucket_level_2 || '—'}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  {/* No Bucket Categories */}
                  {categoriesByBucket.noBucket.categories.length > 0 && (
                    <>
                      <tr className={styles.bucketGroupHeader}>
                        <td colSpan={3}>No Bucket Assigned ({categoriesByBucket.noBucket.total})</td>
                      </tr>
                      {categoriesByBucket.noBucket.categories.map((cat) => (
                        <tr key={cat.id}>
                          <td>{cat.name}</td>
                          <td>{cat.bucket_level_1 || '—'}</td>
                          <td>{cat.bucket_level_2 || '—'}</td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.emptyText}>No categories available</p>
          )}
        </SectionBlock>

        <SectionBlock
          title="Contacts"
          icon="☎️"
          secondaryAction={sectionAction('contacts')}
        >
          <div className={styles.twoColGrid}>
            <FieldRow
              label="Contact Name"
              value={isEditing('contacts') && contactsDraft ? contactsDraft.contact_name : data.contact_name}
              editable={isEditing('contacts')}
              onChange={(value) => isEditing('contacts') && setContactsDraft((draft) => draft ? { ...draft, contact_name: value } : null)}
            />
            <FieldRow
              label="Contact Email"
              value={isEditing('contacts') && contactsDraft ? contactsDraft.contact_email : data.contact_email}
              editable={isEditing('contacts')}
              onChange={(value) => isEditing('contacts') && setContactsDraft((draft) => draft ? { ...draft, contact_email: value } : null)}
            />
            <FieldRow
              label="Contact Phone"
              value={isEditing('contacts') && contactsDraft ? contactsDraft.contact_phone : data.contact_phone}
              editable={isEditing('contacts')}
              onChange={(value) => isEditing('contacts') && setContactsDraft((draft) => draft ? { ...draft, contact_phone: value } : null)}
            />
            <FieldRow
              label="Physical Address"
              value={isEditing('contacts') && contactsDraft ? contactsDraft.physical_address : data.physical_address}
              editable={isEditing('contacts')}
              multiline
              onChange={(value) => isEditing('contacts') && setContactsDraft((draft) => draft ? { ...draft, physical_address: value } : null)}
              className={styles.fullWidth}
            />
            <FieldRow
              label="Contact Notes"
              value={isEditing('contacts') && contactsDraft ? contactsDraft.contact_notes : data.contact_notes}
              editable={isEditing('contacts')}
              multiline
              onChange={(value) => isEditing('contacts') && setContactsDraft((draft) => draft ? { ...draft, contact_notes: value } : null)}
              className={styles.fullWidth}
            />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Judging"
          icon="⚖️"
          secondaryAction={sectionAction('judging')}
        >
          <div className={styles.twoColGrid}>
            <FieldRow label="Celebration Type" value={data.celebration_type} />
            <FieldRow label="Judging Criteria" value={data.judging_criteria} className={styles.fullWidth} multiline />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Program Details"
          icon="📦"
          secondaryAction={sectionAction('details')}
        >
          <div className={styles.twoColGrid}>
            <FieldRow
              label="Host Country"
              value={isEditing('details') && detailsDraft ? detailsDraft.host_country : data.host_country}
              editable={isEditing('details')}
              onChange={(value) => isEditing('details') && setDetailsDraft((draft) => draft ? { ...draft, host_country: value } : null)}
            />
            <FieldRow
              label="Language Requirements"
              value={isEditing('details') && detailsDraft ? detailsDraft.language_requirements : data.language_requirements}
              editable={isEditing('details')}
              onChange={(value) => isEditing('details') && setDetailsDraft((draft) => draft ? { ...draft, language_requirements: value } : null)}
            />
            <FieldRow
              label="Submission Type"
              value={isEditing('details') && detailsDraft ? detailsDraft.submission_type : data.submission_type}
              editable={isEditing('details')}
              onChange={(value) => isEditing('details') && setDetailsDraft((draft) => draft ? { ...draft, submission_type: value } : null)}
            />
            <FieldRow
              label="Audiobook Categories"
              value={
                isEditing('details') && detailsDraft
                  ? detailsDraft.has_audiobook_categories
                  : formatBooleanDisplay(data.has_audiobook_categories)
              }
              editable={isEditing('details')}
              onChange={(value) => isEditing('details') && setDetailsDraft((draft) => draft ? { ...draft, has_audiobook_categories: value } : null)}
            />
            <FieldRow
              label="Industry Warnings"
              value={isEditing('details') && detailsDraft ? detailsDraft.industry_warnings : data.industry_warnings}
              editable={isEditing('details')}
              multiline
              onChange={(value) => isEditing('details') && setDetailsDraft((draft) => draft ? { ...draft, industry_warnings: value } : null)}
              className={styles.fullWidth}
            />
          </div>
        </SectionBlock>

        <SectionBlock
          title="Vetting"
          icon="✅"
          secondaryAction={sectionAction('vetting')}
        >
          <div className={styles.vettingActions}>
            {vettingOptions.map((option) => {
              const selected = data.program_vetting_status === option.status;
              return (
                <button
                  key={option.status}
                  type="button"
                  className={`${styles.vettingOption} ${selected ? styles.vettingOptionSelected : ''}`}
                  onClick={() => handleVettingStatusChange(option.status)}
                  disabled={savingSection === 'vetting'}
                >
                  {selected ? '• ' : ''}{option.label}
                </button>
              );
            })}
          </div>

          <FieldRow
            label="Vetting Notes"
            value={isEditing('vetting') && vettingDraft ? vettingDraft.vetting_notes : data.vetting_notes}
            editable={isEditing('vetting')}
            multiline
            onChange={(value) => {
              if (!isEditing('vetting')) return;
              setVettingDraft((draft) => draft ? { ...draft, vetting_notes: value } : null);
            }}
          />

          <div className={styles.qcStatusRow}>
            <label className={styles.qcStatusLabel}>
              <input
                type="checkbox"
                checked={
                  isEditing('vetting') && vettingDraft
                    ? (vettingDraft.qc_flag_vetting ?? false)
                    : (data.qc_flag_vetting ?? false)
                }
                onChange={(e) => {
                  if (!isEditing('vetting')) return;
                  setVettingDraft((draft) => draft ? { ...draft, qc_flag_vetting: e.target.checked } : null);
                }}
                disabled={!isEditing('vetting')}
                className={styles.qcCheckbox}
              />
              <span>QC Review Completed</span>
            </label>
            {isEditing('vetting') && vettingDraft?.qc_flag_vetting && (
              <input
                type="text"
                className={styles.qcNotesInput}
                placeholder="Optional QC notes..."
                value={vettingDraft.qc_flag_vetting_notes ?? ''}
                onChange={(e) => setVettingDraft((draft) => draft ? { ...draft, qc_flag_vetting_notes: e.target.value } : null)}
              />
            )}
            {!isEditing('vetting') && data.qc_flag_vetting && data.qc_flag_vetting_notes && (
              <span className={styles.qcNotesDisplay}>{data.qc_flag_vetting_notes}</span>
            )}
          </div>
        </SectionBlock>
      </div>
    </div>
  );
};

export default ProgramCardPrint;
